import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Scale,
  Users,
  Search,
  Filter,
  Calendar,
  Building2,
  Activity,
  DollarSign,
  AlertTriangle,
  ShieldAlert,
  Info,
  ChevronDown,
  ArrowUpRight,
  ArrowDownRight,
  ExternalLink,
  RefreshCw,
  X,
  FileSpreadsheet,
  CheckCircle2,
  Stethoscope,
  ChevronLeft,
  ChevronRight,
  BarChart3,
  TrendingUp,
  Percent,
  SlidersHorizontal,
  Layers,
  HelpCircle,
  Download
} from 'lucide-react';
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  Area
} from 'recharts';
import apiService from '../services/apiService';
import { formatCurrency } from '../utils/formatters';
import {
  PhysicianBenchmarkData,
  PhysicianMeta,
  TaxonomyItem,
  DrilldownClaim,
  DrilldownResponse
} from '../types/benchmark';

interface PhysicianBenchmarkingViewProps {
  initialPhysicianName?: string;
  onSelectPhysician?: (name: string) => void;
}

export const PhysicianBenchmarkingView: React.FC<PhysicianBenchmarkingViewProps> = ({
  initialPhysicianName,
  onSelectPhysician,
}) => {
  // Directory & taxonomies
  const [physiciansList, setPhysiciansList] = useState<PhysicianMeta[]>([]);
  const [taxonomies, setTaxonomies] = useState<TaxonomyItem[]>([]);
  const [facilities, setFacilities] = useState<string[]>([]);
  const [loadingDirectory, setLoadingDirectory] = useState<boolean>(true);

  // Selected filters
  const [selectedPhysician, setSelectedPhysician] = useState<string>(initialPhysicianName || '');
  const [physicianSearch, setPhysicianSearch] = useState<string>('');
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

  const [selectedSpecialty, setSelectedSpecialty] = useState<string>('');
  const [selectedSubSpecialty, setSelectedSubSpecialty] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('2025-05-01');
  const [dateTo, setDateTo] = useState<string>('2025-06-30');
  const [selectedFacility, setSelectedFacility] = useState<string>('all');
  const [patientType, setPatientType] = useState<string>('all'); // 'all', 'inpatient', 'outpatient'

  // Benchmark Report Data
  const [benchmarkData, setBenchmarkData] = useState<PhysicianBenchmarkData | null>(null);
  const [loadingReport, setLoadingReport] = useState<boolean>(false);
  const [reportError, setReportError] = useState<string | null>(null);

  // Active sub-tab within benchmarking
  const [activeTab, setActiveTab] = useState<'overview' | 'labs' | 'icd' | 'cpt' | 'casemix' | 'financial' | 'risk'>('overview');

  // Trend Metric Switcher
  const [trendMetric, setTrendMetric] = useState<'amount' | 'claims' | 'fraud'>('amount');

  // Drilldown Modal State
  const [drilldownOpen, setDrilldownOpen] = useState<boolean>(false);
  const [drilldownLoading, setDrilldownLoading] = useState<boolean>(false);
  const [drilldownData, setDrilldownData] = useState<DrilldownResponse | null>(null);
  const [drilldownTitle, setDrilldownTitle] = useState<string>('');
  const [drilldownMetricType, setDrilldownMetricType] = useState<string>('all');
  const [drilldownMetricValue, setDrilldownMetricValue] = useState<string>('');
  const [drilldownPage, setDrilldownPage] = useState<number>(1);
  const [drilldownSearch, setDrilldownSearch] = useState<string>('');

  // Tooltip & info banner dismissal
  const [showMethodologyTooltip, setShowMethodologyTooltip] = useState<boolean>(false);

  // 1. Load initial directory, taxonomies, and facilities
  useEffect(() => {
    let isMounted = true;
    const initData = async () => {
      try {
        setLoadingDirectory(true);
        const [dirRes, taxRes, facRes] = await Promise.all([
          apiService.getPhysiciansBenchmarkDirectory().catch(() => ({ physicians: [] })),
          apiService.getSpecialtyTaxonomies().catch(() => ({ taxonomies: [] })),
          apiService.getFacilitiesList().catch(() => ({ facilities: [] })),
        ]);

        if (!isMounted) return;

        const docs = dirRes.physicians || [];
        setPhysiciansList(docs);
        setTaxonomies(taxRes.taxonomies || []);
        setFacilities(facRes.facilities || []);

        // Default to provided name or first doctor with claims
        if (initialPhysicianName) {
          setSelectedPhysician(initialPhysicianName);
        } else if (docs.length > 0 && !selectedPhysician) {
          setSelectedPhysician(docs[0].physician_name);
        }
      } catch (err) {
        console.error('Error loading benchmarking directory:', err);
      } finally {
        if (isMounted) setLoadingDirectory(false);
      }
    };
    initData();
    return () => {
      isMounted = false;
    };
  }, [initialPhysicianName]);

  // Update selected physician when prop changes
  useEffect(() => {
    if (initialPhysicianName && initialPhysicianName !== selectedPhysician) {
      setSelectedPhysician(initialPhysicianName);
    }
  }, [initialPhysicianName]);

  // 2. Fetch Benchmark Data when filters change
  const loadBenchmark = useCallback(async () => {
    if (!selectedPhysician) return;
    try {
      setLoadingReport(true);
      setReportError(null);
      const data = await apiService.getPhysicianBenchmark(selectedPhysician, {
        specialty: selectedSpecialty || undefined,
        sub_specialty: selectedSubSpecialty || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        facility: selectedFacility,
        patient_type: patientType,
        min_peers: 10,
      });

      setBenchmarkData(data);

      // Auto-populate specialty dropdowns if currently empty
      if (!selectedSpecialty && data?.peer_group?.specialty) {
        setSelectedSpecialty(data.peer_group.specialty);
      }
      if (!selectedSubSpecialty && data?.peer_group?.sub_specialty) {
        setSelectedSubSpecialty(data.peer_group.sub_specialty);
      }
    } catch (err: any) {
      setReportError(err?.message || 'Failed to generate physician benchmark');
    } finally {
      setLoadingReport(false);
    }
  }, [
    selectedPhysician,
    selectedSpecialty,
    selectedSubSpecialty,
    dateFrom,
    dateTo,
    selectedFacility,
    patientType,
  ]);

  useEffect(() => {
    loadBenchmark();
  }, [loadBenchmark]);

  // When doctor changes, reset specialty override so detected specialty takes precedence
  const handlePhysicianSelect = (docName: string) => {
    setSelectedPhysician(docName);
    setSelectedSpecialty('');
    setSelectedSubSpecialty('');
    setIsDropdownOpen(false);
    setPhysicianSearch('');
    if (onSelectPhysician) onSelectPhysician(docName);
  };

  // 3. Drill-down Handler
  const openDrilldown = async (
    metricType: string,
    metricValue: string,
    title: string,
    page: number = 1
  ) => {
    setDrilldownMetricType(metricType);
    setDrilldownMetricValue(metricValue);
    setDrilldownTitle(title);
    setDrilldownPage(page);
    setDrilldownOpen(true);
    setDrilldownLoading(true);

    try {
      const res = await apiService.getPhysicianDrilldown(selectedPhysician, {
        metric_type: metricType,
        metric_value: metricValue,
        date_from: dateFrom,
        date_to: dateTo,
        facility: selectedFacility,
        page: page,
        page_size: 20,
      });
      setDrilldownData(res);
    } catch (err) {
      console.error('Failed to load drilldown claims:', err);
    } finally {
      setDrilldownLoading(false);
    }
  };

  const handleDrilldownPageChange = (newPage: number) => {
    if (newPage < 1 || (drilldownData && newPage > drilldownData.total_pages)) return;
    openDrilldown(drilldownMetricType, drilldownMetricValue, drilldownTitle, newPage);
  };

  // Filtered physicians in autocomplete dropdown
  const filteredDirectory = useMemo(() => {
    if (!physicianSearch.trim()) return physiciansList.slice(0, 30);
    const q = physicianSearch.toLowerCase();
    return physiciansList.filter(
      (p) =>
        p.physician_name.toLowerCase().includes(q) ||
        p.specialty.toLowerCase().includes(q) ||
        p.sub_specialty.toLowerCase().includes(q)
    ).slice(0, 30);
  }, [physiciansList, physicianSearch]);

  // Sub-specialties available for the currently selected specialty
  const availableSubSpecialties = useMemo(() => {
    if (!selectedSpecialty) return [];
    const tax = taxonomies.find((t) => t.specialty === selectedSpecialty);
    return tax ? tax.sub_specialties : [];
  }, [taxonomies, selectedSpecialty]);

  // Filtered drilldown claims for search input inside modal
  const displayedDrilldownClaims = useMemo(() => {
    if (!drilldownData?.claims) return [];
    if (!drilldownSearch.trim()) return drilldownData.claims;
    const q = drilldownSearch.toLowerCase();
    return drilldownData.claims.filter(
      (c) =>
        c.invoice_no?.toLowerCase().includes(q) ||
        c.patient_name?.toLowerCase().includes(q) ||
        c.primary_diag_code?.toLowerCase().includes(q) ||
        c.cpt_code?.toLowerCase().includes(q) ||
        c.provider_name?.toLowerCase().includes(q)
    );
  }, [drilldownData, drilldownSearch]);

  // Export drilldown to CSV
  const exportDrilldownCsv = () => {
    if (!drilldownData?.claims || drilldownData.claims.length === 0) return;
    const headers = [
      'Invoice No',
      'Date',
      'Patient ID',
      'Patient Name',
      'ICD Code',
      'ICD Description',
      'CPT Code',
      'CPT Description',
      'Category',
      'Type',
      'Gross Amount',
      'Facility',
      'Fraud Flag'
    ];
    const rows = drilldownData.claims.map((c) => [
      `"${c.invoice_no || ''}"`,
      `"${c.service_date || ''}"`,
      `"${c.member_id || ''}"`,
      `"${c.patient_name || ''}"`,
      `"${c.primary_diag_code || ''}"`,
      `"${c.diag_description || ''}"`,
      `"${c.cpt_code || ''}"`,
      `"${c.cpt_description || ''}"`,
      `"${c.service_category || ''}"`,
      `"${c.service_type || ''}"`,
      c.gross_claim_amount,
      `"${c.provider_name || ''}"`,
      c.is_fraud ? 'FLAGGED' : 'CLEAN'
    ]);
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `drilldown_${selectedPhysician.replace(/\s+/g, '_')}_${drilldownMetricValue || 'claims'}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const peerGroup = benchmarkData?.peer_group;
  const kpis = benchmarkData?.kpi_cards;

  return (
    <div className="space-y-6 animate-fade-in text-slate-900 dark:text-slate-100 pb-16">
      {/* ════════════════════════════════════════════════════════════════════════
          1. HEADER & METHODOLOGY NOTICE
         ════════════════════════════════════════════════════════════════════════ */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-2 border-b border-slate-200/80 dark:border-slate-800">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider bg-primary-100 text-primary-800 dark:bg-primary-950/70 dark:text-primary-300 border border-primary-200 dark:border-primary-800">
              <Scale className="w-3.5 h-3.5 text-primary-600 dark:text-primary-400" />
              Clinical Peer Benchmarking
            </span>
            <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400">
              Specialty-Isolated Analytics
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-slate-900 dark:text-white flex items-center gap-3">
            Physician Specialty Benchmarking
            {selectedPhysician && (
              <span className="text-primary-600 dark:text-primary-400 font-extrabold text-xl sm:text-2xl">
                : Dr. {selectedPhysician}
              </span>
            )}
          </h1>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-3xl leading-relaxed">
            Clinically fair comparison strictly against physicians in the same specialty or sub-specialty.
            Global cross-specialty comparisons are prohibited to ensure peer integrity.
          </p>
        </div>

        {/* Methodology Tooltip Trigger & Refresh */}
        <div className="flex items-center gap-2 self-start md:self-center">
          <div className="relative">
            <button
              onClick={() => setShowMethodologyTooltip(!showMethodologyTooltip)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 transition-all border border-slate-200 dark:border-slate-700 cursor-pointer shadow-sm"
              title="View clinical benchmarking methodology"
            >
              <Info className="w-3.5 h-3.5 text-primary-500" />
              <span>Methodology</span>
            </button>

            {showMethodologyTooltip && (
              <div className="absolute right-0 top-11 w-80 sm:w-96 p-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-2xl z-50 animate-scale-in text-xs leading-relaxed space-y-2">
                <div className="flex items-center justify-between pb-2 border-b border-slate-100 dark:border-slate-800">
                  <span className="font-extrabold text-slate-900 dark:text-white flex items-center gap-1.5">
                    <Scale className="w-4 h-4 text-primary-500" />
                    Clinical Benchmarking Rules
                  </span>
                  <button
                    onClick={() => setShowMethodologyTooltip(false)}
                    className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-slate-600 dark:text-slate-300">
                  <strong>Level 1 (Priority):</strong> Selected physician is compared against peers in the exact same sub-specialty (e.g. Cosmetic Dermatology).
                </p>
                <p className="text-slate-600 dark:text-slate-300">
                  <strong>Level 2 (Fallback):</strong> If fewer than 10 peers exist in the sub-specialty, comparison broadens to the parent specialty (e.g. Dermatology).
                </p>
                <p className="text-slate-600 dark:text-slate-300">
                  <strong>Level 3 (Protection):</strong> If fewer than 10 peers exist across the specialty, results are explicitly flagged as limited or unavailable.
                </p>
                <div className="p-2.5 rounded-xl bg-primary-50/80 dark:bg-primary-950/40 text-primary-900 dark:text-primary-300 text-[11px] font-medium border border-primary-200/60 dark:border-primary-800/60">
                  Never compares against global averages or unrelated specialists.
                </div>
              </div>
            )}
          </div>

          <button
            onClick={loadBenchmark}
            disabled={loadingReport}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold bg-primary-600 hover:bg-primary-700 text-white transition-all shadow-md shadow-primary-500/20 cursor-pointer disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loadingReport ? 'animate-spin' : ''}`} />
            <span>Recalculate</span>
          </button>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════════════
          2. BENCHMARKING SCOPE FILTER BAR
         ════════════════════════════════════════════════════════════════════════ */}
      <div className="glass-card rounded-2xl p-4 shadow-sm border border-slate-200/80 dark:border-slate-800 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
          {/* Physician Searchable Selector */}
          <div className="lg:col-span-2 relative">
            <label className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1 block">
              Treating Physician
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="w-full flex items-center justify-between px-3.5 py-2.5 bg-slate-50 dark:bg-slate-800/90 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-900 dark:text-white font-bold hover:border-primary-500 focus:outline-none transition-all shadow-sm text-left"
              >
                <div className="flex items-center gap-2 truncate">
                  <Stethoscope className="w-4 h-4 text-primary-500 shrink-0" />
                  <span className="truncate">
                    {selectedPhysician ? `Dr. ${selectedPhysician}` : 'Select Physician...'}
                  </span>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-400 shrink-0 ml-1" />
              </button>

              {isDropdownOpen && (
                <div className="absolute left-0 right-0 top-12 z-50 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-2xl p-2 animate-scale-in">
                  <div className="relative mb-2">
                    <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-2.5" />
                    <input
                      type="text"
                      value={physicianSearch}
                      onChange={(e) => setPhysicianSearch(e.target.value)}
                      placeholder="Search 316 physicians..."
                      className="w-full pl-9 pr-3 py-1.5 bg-slate-100 dark:bg-slate-800 rounded-xl text-xs text-slate-900 dark:text-white focus:outline-none font-medium"
                      autoFocus
                    />
                  </div>
                  <div className="max-h-60 overflow-y-auto space-y-1">
                    {filteredDirectory.map((p) => (
                      <button
                        key={p.physician_name}
                        onClick={() => handlePhysicianSelect(p.physician_name)}
                        className={`w-full text-left px-3 py-2 rounded-xl text-xs flex items-center justify-between transition-colors cursor-pointer ${
                          selectedPhysician === p.physician_name
                            ? 'bg-primary-50 dark:bg-primary-950/60 text-primary-700 dark:text-primary-300 font-bold'
                            : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200'
                        }`}
                      >
                        <div>
                          <div className="font-bold">Dr. {p.physician_name}</div>
                          <div className="text-[10px] text-slate-400">
                            {p.specialty} • {p.sub_specialty}
                          </div>
                        </div>
                        <span className="text-[10px] font-mono font-bold text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full">
                          {p.total_claims.toLocaleString()} claims
                        </span>
                      </button>
                    ))}
                    {filteredDirectory.length === 0 && (
                      <div className="p-3 text-center text-xs text-slate-400">
                        No physicians matching search
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Specialty Selector (Auto-detected or Override) */}
          <div>
            <label className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1 block">
              Specialty
            </label>
            <select
              value={selectedSpecialty}
              onChange={(e) => {
                setSelectedSpecialty(e.target.value);
                setSelectedSubSpecialty('');
              }}
              className="w-full px-3 py-2.5 bg-slate-50 dark:bg-slate-800/90 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-900 dark:text-white font-bold focus:outline-none focus:ring-2 focus:ring-primary-500 cursor-pointer"
            >
              <option value="">(Detected Specialty)</option>
              {taxonomies.map((t) => (
                <option key={t.specialty} value={t.specialty}>
                  {t.specialty}
                </option>
              ))}
            </select>
          </div>

          {/* Sub-Specialty Selector (Auto-detected or Override) */}
          <div>
            <label className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1 block">
              Sub-Specialty
            </label>
            <select
              value={selectedSubSpecialty}
              onChange={(e) => setSelectedSubSpecialty(e.target.value)}
              disabled={!selectedSpecialty}
              className="w-full px-3 py-2.5 bg-slate-50 dark:bg-slate-800/90 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-900 dark:text-white font-bold focus:outline-none focus:ring-2 focus:ring-primary-500 cursor-pointer disabled:opacity-50"
            >
              <option value="">(Detected Sub-Specialty)</option>
              {availableSubSpecialties.map((sub) => (
                <option key={sub} value={sub}>
                  {sub}
                </option>
              ))}
            </select>
          </div>

          {/* Facility / Provider Filter */}
          <div>
            <label className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1 block">
              Facility / Provider
            </label>
            <select
              value={selectedFacility}
              onChange={(e) => setSelectedFacility(e.target.value)}
              className="w-full px-3 py-2.5 bg-slate-50 dark:bg-slate-800/90 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-900 dark:text-white font-bold focus:outline-none focus:ring-2 focus:ring-primary-500 cursor-pointer"
            >
              <option value="all">All Facilities</option>
              {facilities.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </div>

          {/* Patient Type: All / Inpatient / Outpatient */}
          <div>
            <label className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1 block">
              Patient Type
            </label>
            <select
              value={patientType}
              onChange={(e) => setPatientType(e.target.value)}
              className="w-full px-3 py-2.5 bg-slate-50 dark:bg-slate-800/90 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-900 dark:text-white font-bold focus:outline-none focus:ring-2 focus:ring-primary-500 cursor-pointer"
            >
              <option value="all">All Patients</option>
              <option value="inpatient">Inpatient (I) Only</option>
              <option value="outpatient">Outpatient (O) Only</option>
            </select>
          </div>
        </div>

        {/* Date Range Selector with Quick Presets */}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-slate-100 dark:border-slate-800/80 text-xs">
          <div className="flex items-center gap-2">
            <Calendar className="w-3.5 h-3.5 text-slate-400" />
            <span className="font-bold text-slate-600 dark:text-slate-300">Date Range:</span>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="px-2.5 py-1 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs font-mono font-medium"
            />
            <span className="text-slate-400">to</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="px-2.5 py-1 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs font-mono font-medium"
            />
          </div>

          {/* Quick Presets */}
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mr-1">Presets:</span>
            <button
              onClick={() => {
                setDateFrom('2025-05-01');
                setDateTo('2025-06-30');
              }}
              className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 cursor-pointer"
            >
              Full Q2 2025
            </button>
            <button
              onClick={() => {
                setDateFrom('2025-05-01');
                setDateTo('2025-05-31');
              }}
              className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 cursor-pointer"
            >
              May 2025
            </button>
            <button
              onClick={() => {
                setDateFrom('2025-06-01');
                setDateTo('2025-06-30');
              }}
              className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 cursor-pointer"
            >
              June 2025
            </button>
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════════════
          3. PEER GROUP DEFINITION BANNER & CLINICAL FAIRNESS STATUS
         ════════════════════════════════════════════════════════════════════════ */}
      {peerGroup && (
        <div className="p-4 sm:p-5 rounded-2xl border bg-gradient-to-r from-slate-50 via-indigo-50/40 to-slate-50 dark:from-slate-900 dark:via-indigo-950/30 dark:to-slate-900 border-indigo-200/80 dark:border-indigo-800/80 flex flex-col lg:flex-row lg:items-center justify-between gap-4 shadow-sm animate-fade-in">
          <div className="flex items-start sm:items-center gap-3.5">
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center font-black text-white shrink-0 shadow-md ${
              peerGroup.benchmark_level === 1
                ? 'bg-emerald-600 shadow-emerald-500/20'
                : peerGroup.benchmark_level === 2
                ? 'bg-indigo-600 shadow-indigo-500/20'
                : 'bg-amber-600 shadow-amber-500/20'
            }`}>
              <Users className="w-6 h-6" />
            </div>

            <div className="space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-black uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  Benchmark Population:
                </span>
                <span className="text-sm font-black text-slate-900 dark:text-white">
                  {peerGroup.specialty}
                </span>
                <span className="text-slate-400">•</span>
                <span className="text-xs font-extrabold text-indigo-700 dark:text-indigo-300 bg-indigo-100/80 dark:bg-indigo-900/50 px-2.5 py-0.5 rounded-full">
                  {peerGroup.sub_specialty}
                </span>
                {/* Level Badge */}
                <span className={`text-[10px] font-black px-2.5 py-0.5 rounded-full uppercase tracking-wider flex items-center gap-1 ${
                  peerGroup.benchmark_level === 1
                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700'
                    : peerGroup.benchmark_level === 2
                    ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-300 border border-indigo-300 dark:border-indigo-700'
                    : 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300 border border-amber-300 dark:border-amber-700'
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${
                    peerGroup.benchmark_level === 1 ? 'bg-emerald-500' : peerGroup.benchmark_level === 2 ? 'bg-indigo-500' : 'bg-amber-500'
                  }`} />
                  {peerGroup.level_label}
                </span>
              </div>

              <p className="text-xs text-slate-600 dark:text-slate-300 font-medium">
                {peerGroup.notice_message}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 self-end lg:self-center shrink-0">
            <div className="px-4 py-2 rounded-xl bg-white/90 dark:bg-slate-800/90 border border-slate-200/80 dark:border-slate-700 text-right">
              <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider block">
                Comparable Physicians
              </span>
              <span className="text-lg font-black font-mono text-slate-900 dark:text-white">
                {peerGroup.comparable_physicians_count} Peers
              </span>
            </div>

            <div className="px-4 py-2 rounded-xl bg-white/90 dark:bg-slate-800/90 border border-slate-200/80 dark:border-slate-700 text-right">
              <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider block">
                Evaluation Window
              </span>
              <span className="text-xs font-bold font-mono text-slate-700 dark:text-slate-200">
                {dateFrom.slice(5)} to {dateTo.slice(5)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Limited / Unavailable Alert */}
      {peerGroup?.status === 'limited' && (
        <div className="p-3.5 rounded-xl bg-amber-50 dark:bg-amber-950/40 border border-amber-300 dark:border-amber-800 flex items-center gap-3 text-xs text-amber-900 dark:text-amber-200 font-medium">
          <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
          <span>
            <strong>Limited benchmark data:</strong> Only {peerGroup.comparable_physicians_count} comparable physicians were found. Results provide clinical directional insight but may not reach statistical significance.
          </span>
        </div>
      )}

      {peerGroup?.status === 'unavailable' && (
        <div className="p-4 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-300 dark:border-rose-800 flex items-center gap-3 text-xs text-rose-900 dark:text-rose-200 font-medium">
          <AlertTriangle className="w-5 h-5 text-rose-600 dark:text-rose-400 shrink-0" />
          <div>
            <div className="font-bold">Benchmark unavailable for this specialty/sub-specialty.</div>
            <div>
              There are not enough comparable physicians practicing in this field. To adhere strictly to clinical fairness, this platform refuses to compare doctors against unrelated specialties.
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════════
          4. CORE BENCHMARK KPI CARDS
         ════════════════════════════════════════════════════════════════════════ */}
      {kpis && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Card 1: Average Claim Amount */}
          <div
            onClick={() => openDrilldown('all', '', 'All Physician Encounters')}
            className="glass-card rounded-2xl p-5 border border-slate-200/80 dark:border-slate-800 shadow-sm hover:border-primary-500/60 transition-all cursor-pointer group relative overflow-hidden"
          >
            <div className="flex items-center justify-between text-slate-500 dark:text-slate-400 mb-2">
              <span className="text-xs font-bold uppercase tracking-wider">Average Claim Amount</span>
              <DollarSign className="w-4 h-4 text-emerald-500 group-hover:scale-110 transition-transform" />
            </div>
            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-2xl sm:text-3xl font-black font-mono text-slate-900 dark:text-white">
                {formatCurrency(kpis.average_claim.physician)}
              </span>
              <span className={`text-xs font-black flex items-center ${
                kpis.average_claim.difference_pct > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'
              }`}>
                {kpis.average_claim.difference_pct > 0 ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
                {Math.abs(kpis.average_claim.difference_pct)}% vs peers
              </span>
            </div>
            <div className="pt-2.5 border-t border-slate-100 dark:border-slate-800/80 flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400">
              <span>Peer Avg: <strong className="text-slate-700 dark:text-slate-300 font-mono">{formatCurrency(kpis.average_claim.peer_average)}</strong></span>
              <span className="font-extrabold font-mono px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                {kpis.average_claim.percentile_label} percentile
              </span>
            </div>
          </div>

          {/* Card 2: Lab Utilization */}
          <div
            onClick={() => openDrilldown('lab', '', 'Laboratory Diagnostic Encounters')}
            className="glass-card rounded-2xl p-5 border border-slate-200/80 dark:border-slate-800 shadow-sm hover:border-primary-500/60 transition-all cursor-pointer group relative overflow-hidden"
          >
            <div className="flex items-center justify-between text-slate-500 dark:text-slate-400 mb-2">
              <span className="text-xs font-bold uppercase tracking-wider">Lab Test Utilization</span>
              <Activity className="w-4 h-4 text-blue-500 group-hover:scale-110 transition-transform" />
            </div>
            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-2xl sm:text-3xl font-black font-mono text-slate-900 dark:text-white">
                {kpis.lab_utilization.physician.toFixed(1)}
              </span>
              <span className="text-xs text-slate-400 font-semibold">/ case</span>
              <span className={`text-xs font-black flex items-center ml-auto ${
                kpis.lab_utilization.difference_pct > 25 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'
              }`}>
                {kpis.lab_utilization.difference_pct > 0 ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
                {Math.abs(kpis.lab_utilization.difference_pct)}%
              </span>
            </div>
            <div className="pt-2.5 border-t border-slate-100 dark:border-slate-800/80 flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400">
              <span>Peer Avg: <strong className="text-slate-700 dark:text-slate-300 font-mono">{kpis.lab_utilization.peer_average.toFixed(1)} / case</strong></span>
              <span className="font-extrabold font-mono px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                {kpis.lab_utilization.percentile_label} percentile
              </span>
            </div>
          </div>

          {/* Card 3: Procedure Utilization */}
          <div
            onClick={() => openDrilldown('cpt', '', 'Medical Procedures & Service Lines')}
            className="glass-card rounded-2xl p-5 border border-slate-200/80 dark:border-slate-800 shadow-sm hover:border-primary-500/60 transition-all cursor-pointer group relative overflow-hidden"
          >
            <div className="flex items-center justify-between text-slate-500 dark:text-slate-400 mb-2">
              <span className="text-xs font-bold uppercase tracking-wider">Procedure Frequency</span>
              <Layers className="w-4 h-4 text-purple-500 group-hover:scale-110 transition-transform" />
            </div>
            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-2xl sm:text-3xl font-black font-mono text-slate-900 dark:text-white">
                {kpis.procedure_utilization.physician.toFixed(2)}
              </span>
              <span className="text-xs text-slate-400 font-semibold">/ claim</span>
              <span className={`text-xs font-black flex items-center ml-auto ${
                kpis.procedure_utilization.difference_pct > 30 ? 'text-amber-600 dark:text-amber-400' : 'text-slate-600 dark:text-slate-400'
              }`}>
                {kpis.procedure_utilization.difference_pct > 0 ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
                {Math.abs(kpis.procedure_utilization.difference_pct)}%
              </span>
            </div>
            <div className="pt-2.5 border-t border-slate-100 dark:border-slate-800/80 flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400">
              <span>Peer Avg: <strong className="text-slate-700 dark:text-slate-300 font-mono">{kpis.procedure_utilization.peer_average.toFixed(2)} / claim</strong></span>
              <span className="font-extrabold font-mono px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                {kpis.procedure_utilization.percentile_label} percentile
              </span>
            </div>
          </div>

          {/* Card 4: Risk / Fraud Rate */}
          <div
            onClick={() => openDrilldown('risk', '', 'Audit-Flagged Anomaly Claims')}
            className="glass-card rounded-2xl p-5 border border-slate-200/80 dark:border-slate-800 shadow-sm hover:border-primary-500/60 transition-all cursor-pointer group relative overflow-hidden"
          >
            <div className="flex items-center justify-between text-slate-500 dark:text-slate-400 mb-2">
              <span className="text-xs font-bold uppercase tracking-wider">Claims Requiring Review</span>
              <ShieldAlert className="w-4 h-4 text-rose-500 group-hover:scale-110 transition-transform" />
            </div>
            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-2xl sm:text-3xl font-black font-mono text-slate-900 dark:text-white">
                {kpis.fraud_risk_rate.physician.toFixed(1)}%
              </span>
              <span className={`text-xs font-black flex items-center ml-auto ${
                kpis.fraud_risk_rate.physician >= 10 ? 'text-rose-600 dark:text-rose-400' :
                kpis.fraud_risk_rate.physician >= 5 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'
              }`}>
                {kpis.fraud_risk_rate.difference_pct > 0 ? `+${kpis.fraud_risk_rate.difference_pct}%` : `${kpis.fraud_risk_rate.difference_pct}%`} vs peers
              </span>
            </div>
            <div className="pt-2.5 border-t border-slate-100 dark:border-slate-800/80 flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400">
              <span>Peer Avg: <strong className="text-slate-700 dark:text-slate-300 font-mono">{kpis.fraud_risk_rate.peer_average.toFixed(1)}%</strong></span>
              <span className="font-extrabold font-mono px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                {kpis.fraud_risk_rate.percentile_label} percentile
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════════
          5. SECTION NAVIGATION SUB-TABS
         ════════════════════════════════════════════════════════════════════════ */}
      <div className="flex items-center gap-1.5 p-1 bg-slate-100/90 dark:bg-slate-800/90 rounded-xl border border-slate-200 dark:border-slate-700 overflow-x-auto">
        {[
          { id: 'overview', label: 'Overview & Trends', icon: TrendingUp },
          { id: 'labs', label: 'Laboratory Tests', icon: Activity },
          { id: 'icd', label: 'ICD Diagnosis Codes', icon: Stethoscope },
          { id: 'cpt', label: 'CPT / Procedure Codes', icon: Layers },
          { id: 'casemix', label: 'Case Mix (In/Out-Patient)', icon: BarChart3 },
          { id: 'financial', label: 'Financial Behavior', icon: DollarSign },
          { id: 'risk', label: 'Anomaly / Risk Rate', icon: ShieldAlert },
        ].map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                isActive
                  ? 'bg-white dark:bg-slate-700 text-primary-700 dark:text-primary-300 shadow-sm border border-slate-200/60 dark:border-slate-600'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* ════════════════════════════════════════════════════════════════════════
          TAB CONTENT 1: OVERVIEW & TRENDS
         ════════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Peer Percentile Distribution Summary Bar */}
          <div className="glass-card rounded-2xl p-6 border border-slate-200/80 dark:border-slate-800 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-extrabold text-slate-900 dark:text-white">
                  Physician Percentile Positioning within Specialty Peer Cohort
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Where Dr. {selectedPhysician} ranks (1st to 99th percentile) compared to {peerGroup?.comparable_physicians_count} peer {peerGroup?.specialty} physicians.
                </p>
              </div>
              <span className="text-xs font-mono font-bold text-slate-400">
                50th = Specialty Median
              </span>
            </div>

            {kpis && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-2">
                {[
                  { label: 'Claim Amount', p: kpis.average_claim.percentile, color: 'bg-emerald-500' },
                  { label: 'Lab Utilization', p: kpis.lab_utilization.percentile, color: 'bg-blue-500' },
                  { label: 'Procedure Frequency', p: kpis.procedure_utilization.percentile, color: 'bg-purple-500' },
                  { label: 'Audit / Risk Rate', p: kpis.fraud_risk_rate.percentile, color: 'bg-rose-500' },
                ].map((item) => (
                  <div key={item.label} className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-100 dark:border-slate-800 space-y-2">
                    <div className="flex justify-between items-center text-xs">
                      <span className="font-bold text-slate-700 dark:text-slate-300">{item.label}</span>
                      <span className="font-black font-mono text-primary-600 dark:text-primary-400">{item.p}th %</span>
                    </div>
                    <div className="w-full bg-slate-200 dark:bg-slate-700 h-2 rounded-full overflow-hidden relative">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${item.color}`}
                        style={{ width: `${item.p}%` }}
                      />
                      {/* 50th median tick */}
                      <div className="absolute top-0 bottom-0 left-1/2 w-0.5 bg-slate-400/80" title="Median" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Longitudinal Trend Chart */}
          {benchmarkData?.trends && benchmarkData.trends.length > 0 && (
            <div className="glass-card rounded-2xl p-6 border border-slate-200/80 dark:border-slate-800 shadow-sm space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h3 className="text-base font-extrabold text-slate-900 dark:text-white flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-primary-500" />
                    Longitudinal Specialty Benchmark Trend
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Comparing weekly physician progression against peer cohort benchmark average.
                  </p>
                </div>

                <div className="flex items-center gap-1.5 p-1 bg-slate-100 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 self-start sm:self-auto">
                  <button
                    onClick={() => setTrendMetric('amount')}
                    className={`px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                      trendMetric === 'amount'
                        ? 'bg-white dark:bg-slate-700 text-primary-700 dark:text-primary-300 shadow-sm'
                        : 'text-slate-600 dark:text-slate-400'
                    }`}
                  >
                    Avg Claim ($)
                  </button>
                  <button
                    onClick={() => setTrendMetric('claims')}
                    className={`px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                      trendMetric === 'claims'
                        ? 'bg-white dark:bg-slate-700 text-primary-700 dark:text-primary-300 shadow-sm'
                        : 'text-slate-600 dark:text-slate-400'
                    }`}
                  >
                    Claims Volume
                  </button>
                  <button
                    onClick={() => setTrendMetric('fraud')}
                    className={`px-3 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                      trendMetric === 'fraud'
                        ? 'bg-white dark:bg-slate-700 text-primary-700 dark:text-primary-300 shadow-sm'
                        : 'text-slate-600 dark:text-slate-400'
                    }`}
                  >
                    Risk Rate (%)
                  </button>
                </div>
              </div>

              <div className="h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={benchmarkData.trends} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#94a3b8" opacity={0.15} />
                    <XAxis dataKey="period" stroke="#94a3b8" fontSize={11} />
                    <YAxis stroke="#94a3b8" fontSize={11} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#0f172a',
                        border: '1px solid #334155',
                        borderRadius: '1rem',
                        fontSize: '12px',
                        color: '#fff',
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }} />

                    {trendMetric === 'amount' && (
                      <>
                        <Bar
                          dataKey="physician_avg_claim"
                          name={`Dr. ${selectedPhysician} ($)`}
                          fill="#3b82f6"
                          radius={[6, 6, 0, 0]}
                        />
                        <Line
                          type="monotone"
                          dataKey="peer_avg_claim"
                          name="Peer Average ($)"
                          stroke="#10b981"
                          strokeWidth={3}
                          dot={{ r: 4 }}
                        />
                      </>
                    )}

                    {trendMetric === 'claims' && (
                      <>
                        <Bar
                          dataKey="physician_claims"
                          name={`Dr. ${selectedPhysician} Claims`}
                          fill="#8b5cf6"
                          radius={[6, 6, 0, 0]}
                        />
                        <Line
                          type="monotone"
                          dataKey="peer_avg_claims"
                          name="Peer Avg Claims"
                          stroke="#f59e0b"
                          strokeWidth={3}
                          dot={{ r: 4 }}
                        />
                      </>
                    )}

                    {trendMetric === 'fraud' && (
                      <>
                        <Area
                          type="monotone"
                          dataKey="physician_fraud_rate"
                          name={`Dr. ${selectedPhysician} Risk %`}
                          fill="#f43f5e"
                          fillOpacity={0.2}
                          stroke="#f43f5e"
                          strokeWidth={2}
                        />
                        <Line
                          type="monotone"
                          dataKey="peer_fraud_rate"
                          name="Peer Benchmark Risk %"
                          stroke="#64748b"
                          strokeWidth={2}
                          strokeDasharray="4 4"
                          dot={{ r: 4 }}
                        />
                      </>
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════════
          TAB CONTENT 2: LABORATORY TESTS
         ════════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'labs' && (
        <div className="space-y-6">
          {/* Summary metrics header */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="glass-card rounded-2xl p-4 border border-slate-200 dark:border-slate-800">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Total Lab Tests</span>
              <span className="text-xl font-black font-mono text-slate-900 dark:text-white">
                {benchmarkData?.laboratory?.summary?.total_lab_tests?.toLocaleString()}
              </span>
              <span className="text-[11px] text-slate-500 block mt-1">
                Peer cohort avg: {benchmarkData?.laboratory?.summary?.peer_avg_total_labs}
              </span>
            </div>

            <div className="glass-card rounded-2xl p-4 border border-slate-200 dark:border-slate-800">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Labs Per Case</span>
              <span className="text-xl font-black font-mono text-slate-900 dark:text-white">
                {benchmarkData?.laboratory?.summary?.labs_per_case}
              </span>
              <span className="text-[11px] text-slate-500 block mt-1">
                Peer avg: {benchmarkData?.laboratory?.summary?.peer_labs_per_case}
              </span>
            </div>

            <div className="glass-card rounded-2xl p-4 border border-slate-200 dark:border-slate-800">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Labs Per Claim</span>
              <span className="text-xl font-black font-mono text-slate-900 dark:text-white">
                {benchmarkData?.laboratory?.summary?.labs_per_claim}
              </span>
              <span className="text-[11px] text-slate-500 block mt-1">
                Peer avg: {benchmarkData?.laboratory?.summary?.peer_labs_per_claim}
              </span>
            </div>

            <div className="glass-card rounded-2xl p-4 border border-slate-200 dark:border-slate-800">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Lab Utilization Rate</span>
              <span className="text-xl font-black font-mono text-slate-900 dark:text-white">
                {benchmarkData?.laboratory?.summary?.utilization_rate_pct}%
              </span>
              <span className="text-[11px] text-emerald-600 dark:text-emerald-400 block mt-1">
                Claims with lab orders
              </span>
            </div>
          </div>

          {/* Top ordered lab tests comparative table */}
          <div className="glass-card rounded-2xl p-5 border border-slate-200/80 dark:border-slate-800 shadow-sm space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-extrabold text-slate-900 dark:text-white flex items-center gap-2">
                  <Activity className="w-4 h-4 text-primary-500" />
                  Most Frequently Ordered Laboratory Tests vs Peer Average
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Click any lab test row to inspect individual patient claims and test results.
                </p>
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-slate-200/80 dark:border-slate-800">
              <table className="w-full text-xs text-left">
                <thead className="bg-slate-50 dark:bg-slate-800/80 text-[10px] uppercase font-black tracking-wider text-slate-400 border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th className="py-3 px-4">CPT / Lab Code</th>
                    <th className="py-3 px-4">Clinical Description</th>
                    <th className="py-3 px-4 text-right">Physician Orders</th>
                    <th className="py-3 px-4 text-right">Peer Average</th>
                    <th className="py-3 px-4 text-right">Difference %</th>
                    <th className="py-3 px-4 text-right">Percentile</th>
                    <th className="py-3 px-4">Clinical Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800/80 font-medium">
                  {benchmarkData?.laboratory?.top_ordered_tests?.map((lab) => (
                    <tr
                      key={lab.code}
                      onClick={() => openDrilldown('lab', lab.code, `Lab Test: ${lab.code} (${lab.description})`)}
                      className="hover:bg-primary-50/50 dark:hover:bg-primary-950/20 transition-colors cursor-pointer group"
                    >
                      <td className="py-3 px-4 font-mono font-bold text-primary-600 dark:text-primary-400 flex items-center gap-1.5">
                        <span>{lab.code}</span>
                        <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </td>
                      <td className="py-3 px-4 text-slate-800 dark:text-slate-200 font-semibold max-w-xs truncate">
                        {lab.description}
                      </td>
                      <td className="py-3 px-4 text-right font-mono font-black text-slate-900 dark:text-white">
                        {lab.physician_usage.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right font-mono text-slate-500 dark:text-slate-400">
                        {lab.peer_average}
                      </td>
                      <td className={`py-3 px-4 text-right font-mono font-bold ${
                        lab.difference_pct > 25 ? 'text-amber-600 dark:text-amber-400' :
                        lab.difference_pct < -25 ? 'text-blue-600 dark:text-blue-400' : 'text-slate-600 dark:text-slate-400'
                      }`}>
                        {lab.difference_pct > 0 ? `+${lab.difference_pct}%` : `${lab.difference_pct}%`}
                      </td>
                      <td className="py-3 px-4 text-right font-mono font-bold text-slate-700 dark:text-slate-300">
                        {lab.percentile}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider ${
                          lab.badge_type === 'warning'
                            ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
                            : lab.badge_type === 'info'
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300'
                            : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
                        }`}>
                          {lab.variance_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {(!benchmarkData?.laboratory?.top_ordered_tests || benchmarkData.laboratory.top_ordered_tests.length === 0) && (
                    <tr>
                      <td colSpan={7} className="py-6 text-center text-slate-400">
                        No laboratory tests recorded in this evaluation scope.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════════
          TAB CONTENT 3: ICD CODES
         ════════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'icd' && (
        <div className="space-y-6">
          <div className="glass-card rounded-2xl p-5 border border-slate-200/80 dark:border-slate-800 shadow-sm space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-extrabold text-slate-900 dark:text-white flex items-center gap-2">
                  <Stethoscope className="w-4 h-4 text-primary-500" />
                  Primary ICD Diagnosis Frequency & Distribution vs Peers
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Neutral variance indicators highlight clinical case mix shifts without accusing providers of improper coding.
                </p>
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-slate-200/80 dark:border-slate-800">
              <table className="w-full text-xs text-left">
                <thead className="bg-slate-50 dark:bg-slate-800/80 text-[10px] uppercase font-black tracking-wider text-slate-400 border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th className="py-3 px-4">ICD-10 Code</th>
                    <th className="py-3 px-4">Diagnosis Description</th>
                    <th className="py-3 px-4 text-right">Physician Cases</th>
                    <th className="py-3 px-4 text-right">% of Practice</th>
                    <th className="py-3 px-4 text-right">Peer Avg Usage</th>
                    <th className="py-3 px-4 text-right">Difference %</th>
                    <th className="py-3 px-4 text-right">Percentile</th>
                    <th className="py-3 px-4">Peer Assessment</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800/80 font-medium">
                  {benchmarkData?.icd_codes?.diagnosis_patterns?.map((icd) => (
                    <tr
                      key={icd.code}
                      onClick={() => openDrilldown('icd', icd.code, `ICD Diagnosis: ${icd.code} (${icd.description})`)}
                      className="hover:bg-primary-50/50 dark:hover:bg-primary-950/20 transition-colors cursor-pointer group"
                    >
                      <td className="py-3 px-4 font-mono font-bold text-primary-600 dark:text-primary-400 flex items-center gap-1.5">
                        <span>{icd.code}</span>
                        <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </td>
                      <td className="py-3 px-4 text-slate-800 dark:text-slate-200 font-semibold max-w-sm truncate">
                        {icd.description}
                      </td>
                      <td className="py-3 px-4 text-right font-mono font-black text-slate-900 dark:text-white">
                        {icd.physician_usage.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right font-mono text-slate-600 dark:text-slate-300">
                        {icd.physician_share_pct}%
                      </td>
                      <td className="py-3 px-4 text-right font-mono text-slate-500 dark:text-slate-400">
                        {icd.peer_average}
                      </td>
                      <td className={`py-3 px-4 text-right font-mono font-bold ${
                        icd.difference_pct > 50 ? 'text-amber-600 dark:text-amber-400' :
                        icd.difference_pct < -50 ? 'text-blue-600 dark:text-blue-400' : 'text-slate-600 dark:text-slate-400'
                      }`}>
                        {icd.difference_pct > 0 ? `+${icd.difference_pct}%` : `${icd.difference_pct}%`}
                      </td>
                      <td className="py-3 px-4 text-right font-mono font-bold text-slate-700 dark:text-slate-300">
                        {icd.percentile}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider ${
                          icd.badge_type === 'warning'
                            ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
                            : icd.badge_type === 'review'
                            ? 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300'
                            : icd.badge_type === 'info'
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300'
                            : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
                        }`}>
                          {icd.variance_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {(!benchmarkData?.icd_codes?.diagnosis_patterns || benchmarkData.icd_codes.diagnosis_patterns.length === 0) && (
                    <tr>
                      <td colSpan={8} className="py-6 text-center text-slate-400">
                        No ICD diagnosis codes available in this scope.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════════
          TAB CONTENT 4: CPT / PROCEDURE CODES
         ════════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'cpt' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="glass-card rounded-2xl p-4 border border-slate-200 dark:border-slate-800">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Total Procedures</span>
              <span className="text-xl font-black font-mono text-slate-900 dark:text-white">
                {benchmarkData?.cpt_codes?.summary?.total_procedures?.toLocaleString()}
              </span>
            </div>
            <div className="glass-card rounded-2xl p-4 border border-slate-200 dark:border-slate-800">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Procedures per Claim</span>
              <span className="text-xl font-black font-mono text-slate-900 dark:text-white">
                {benchmarkData?.cpt_codes?.summary?.procedures_per_claim}
              </span>
              <span className="text-[11px] text-slate-500 block mt-1">
                Peer avg: {benchmarkData?.cpt_codes?.summary?.peer_procs_per_claim}
              </span>
            </div>
            <div className="glass-card rounded-2xl p-4 border border-slate-200 dark:border-slate-800">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Procedures per Patient</span>
              <span className="text-xl font-black font-mono text-slate-900 dark:text-white">
                {benchmarkData?.cpt_codes?.summary?.procedures_per_case}
              </span>
            </div>
          </div>

          <div className="glass-card rounded-2xl p-5 border border-slate-200/80 dark:border-slate-800 shadow-sm space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-extrabold text-slate-900 dark:text-white flex items-center gap-2">
                  <Layers className="w-4 h-4 text-primary-500" />
                  Most Frequently Billed CPT & Procedure Codes vs Peer Average
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Click any procedure to inspect patient encounter specifics and quantities.
                </p>
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-slate-200/80 dark:border-slate-800">
              <table className="w-full text-xs text-left">
                <thead className="bg-slate-50 dark:bg-slate-800/80 text-[10px] uppercase font-black tracking-wider text-slate-400 border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th className="py-3 px-4">CPT / Service Code</th>
                    <th className="py-3 px-4">Procedure Description</th>
                    <th className="py-3 px-4 text-right">Physician Usage</th>
                    <th className="py-3 px-4 text-right">Peer Average</th>
                    <th className="py-3 px-4 text-right">Difference %</th>
                    <th className="py-3 px-4 text-right">Percentile</th>
                    <th className="py-3 px-4">Utilization Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800/80 font-medium">
                  {benchmarkData?.cpt_codes?.top_billed_procedures?.map((cpt) => (
                    <tr
                      key={cpt.code}
                      onClick={() => openDrilldown('cpt', cpt.code, `CPT Procedure: ${cpt.code} (${cpt.description})`)}
                      className="hover:bg-primary-50/50 dark:hover:bg-primary-950/20 transition-colors cursor-pointer group"
                    >
                      <td className="py-3 px-4 font-mono font-bold text-primary-600 dark:text-primary-400 flex items-center gap-1.5">
                        <span>{cpt.code}</span>
                        <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </td>
                      <td className="py-3 px-4 text-slate-800 dark:text-slate-200 font-semibold max-w-sm truncate">
                        {cpt.description}
                      </td>
                      <td className="py-3 px-4 text-right font-mono font-black text-slate-900 dark:text-white">
                        {cpt.physician_usage.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right font-mono text-slate-500 dark:text-slate-400">
                        {cpt.peer_average}
                      </td>
                      <td className={`py-3 px-4 text-right font-mono font-bold ${
                        cpt.difference_pct > 40 ? 'text-amber-600 dark:text-amber-400' :
                        cpt.difference_pct < -40 ? 'text-blue-600 dark:text-blue-400' : 'text-slate-600 dark:text-slate-400'
                      }`}>
                        {cpt.difference_pct > 0 ? `+${cpt.difference_pct}%` : `${cpt.difference_pct}%`}
                      </td>
                      <td className="py-3 px-4 text-right font-mono font-bold text-slate-700 dark:text-slate-300">
                        {cpt.percentile}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider ${
                          cpt.badge_type === 'warning'
                            ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300'
                            : cpt.badge_type === 'info'
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300'
                            : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
                        }`}>
                          {cpt.variance_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {(!benchmarkData?.cpt_codes?.top_billed_procedures || benchmarkData.cpt_codes.top_billed_procedures.length === 0) && (
                    <tr>
                      <td colSpan={7} className="py-6 text-center text-slate-400">
                        No procedure codes available in this scope.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════════
          TAB CONTENT 5: CASE MIX (INPATIENT VS OUTPATIENT)
         ════════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'casemix' && benchmarkData?.case_mix && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Case Mix Comparison Visual Bar */}
            <div className="glass-card rounded-2xl p-6 border border-slate-200 dark:border-slate-800 space-y-4">
              <h3 className="text-sm font-extrabold text-slate-900 dark:text-white">
                Inpatient vs Outpatient Admission Ratio
              </h3>

              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-xs font-bold mb-1">
                    <span>Dr. {selectedPhysician}</span>
                    <span className="font-mono">
                      {benchmarkData.case_mix.physician.inpatient_pct}% Inpatient • {benchmarkData.case_mix.physician.outpatient_pct}% Outpatient
                    </span>
                  </div>
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden flex">
                    <div
                      style={{ width: `${benchmarkData.case_mix.physician.inpatient_pct}%` }}
                      className="bg-indigo-600 h-full"
                      title="Inpatient"
                    />
                    <div
                      style={{ width: `${benchmarkData.case_mix.physician.outpatient_pct}%` }}
                      className="bg-emerald-500 h-full"
                      title="Outpatient"
                    />
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-xs font-bold mb-1 text-slate-500">
                    <span>Peer Benchmark Average</span>
                    <span className="font-mono">
                      {benchmarkData.case_mix.peer_average.inpatient_pct ?? 0}% Inpatient • {benchmarkData.case_mix.peer_average.outpatient_pct ?? 100}% Outpatient
                    </span>
                  </div>
                  <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden flex opacity-75">
                    <div
                      style={{ width: `${benchmarkData.case_mix.peer_average.inpatient_pct ?? 0}%` }}
                      className="bg-indigo-600 h-full"
                    />
                    <div
                      style={{ width: `${benchmarkData.case_mix.peer_average.outpatient_pct ?? 100}%` }}
                      className="bg-emerald-500 h-full"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4 text-xs pt-2">
                <span className="flex items-center gap-1.5 font-bold">
                  <span className="w-3 h-3 rounded-full bg-indigo-600" />
                  Inpatient Admissions
                </span>
                <span className="flex items-center gap-1.5 font-bold">
                  <span className="w-3 h-3 rounded-full bg-emerald-500" />
                  Outpatient / Ambulatory
                </span>
              </div>
            </div>

            {/* Inpatient vs Outpatient Metrics Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div
                onClick={() => openDrilldown('inpatient', '', 'Inpatient Admission Claims')}
                className="glass-card rounded-2xl p-4 border border-slate-200 dark:border-slate-800 hover:border-indigo-500 cursor-pointer transition-all"
              >
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Inpatient Encounters</span>
                <span className="text-xl font-black font-mono text-indigo-600 dark:text-indigo-400">
                  {benchmarkData.case_mix.physician.inpatient_cases?.toLocaleString()}
                </span>
                <span className="text-[11px] text-slate-500 block mt-1">
                  Avg Amount: {formatCurrency(benchmarkData.case_mix.physician.inpatient_avg_amount || 0)}
                </span>
              </div>

              <div
                onClick={() => openDrilldown('outpatient', '', 'Outpatient Ambulatory Claims')}
                className="glass-card rounded-2xl p-4 border border-slate-200 dark:border-slate-800 hover:border-emerald-500 cursor-pointer transition-all"
              >
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Outpatient Encounters</span>
                <span className="text-xl font-black font-mono text-emerald-600 dark:text-emerald-400">
                  {benchmarkData.case_mix.physician.outpatient_cases?.toLocaleString()}
                </span>
                <span className="text-[11px] text-slate-500 block mt-1">
                  Avg Amount: {formatCurrency(benchmarkData.case_mix.physician.outpatient_avg_amount || 0)}
                </span>
              </div>

              <div className="glass-card rounded-2xl p-4 border border-slate-200 dark:border-slate-800">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Claims Per Case</span>
                <span className="text-xl font-black font-mono text-slate-900 dark:text-white">
                  {benchmarkData.case_mix.physician.claims_per_case}
                </span>
                <span className="text-[11px] text-slate-500 block mt-1">
                  Peer avg: {benchmarkData.case_mix.peer_average.claims_per_case}
                </span>
              </div>

              <div className="glass-card rounded-2xl p-4 border border-slate-200 dark:border-slate-800">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Total Patients / Cases</span>
                <span className="text-xl font-black font-mono text-slate-900 dark:text-white">
                  {benchmarkData.case_mix.physician.total_cases?.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════════
          TAB CONTENT 6: FINANCIAL BEHAVIOR
         ════════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'financial' && benchmarkData?.financial && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="glass-card rounded-2xl p-4 border border-slate-200 dark:border-slate-800">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Mean Claim Amount</span>
              <span className="text-2xl font-black font-mono text-slate-900 dark:text-white">
                {formatCurrency(benchmarkData.financial.average_claim_amount)}
              </span>
              <span className="text-[11px] text-slate-500 block mt-1">
                Peer mean: {formatCurrency(benchmarkData.financial.peer_average_claim_amount || 0)}
              </span>
            </div>

            <div className="glass-card rounded-2xl p-4 border border-slate-200 dark:border-slate-800">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Median Claim Amount</span>
              <span className="text-2xl font-black font-mono text-emerald-600 dark:text-emerald-400">
                {formatCurrency(benchmarkData.financial.median_claim_amount)}
              </span>
              <span className="text-[11px] text-slate-500 block mt-1">
                Peer median: {formatCurrency(benchmarkData.financial.peer_median_claim_amount || 0)}
              </span>
            </div>

            <div className="glass-card rounded-2xl p-4 border border-slate-200 dark:border-slate-800">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Total Billed Volume</span>
              <span className="text-2xl font-black font-mono text-slate-900 dark:text-white">
                {formatCurrency(benchmarkData.financial.total_billed_amount)}
              </span>
            </div>

            <div className="glass-card rounded-2xl p-4 border border-slate-200 dark:border-slate-800">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Average Amount Per Case</span>
              <span className="text-2xl font-black font-mono text-slate-900 dark:text-white">
                {formatCurrency(benchmarkData.financial.amount_per_case || 0)}
              </span>
              <span className="text-[11px] text-slate-500 block mt-1">
                Peer avg: {formatCurrency(benchmarkData.financial.peer_amount_per_case || 0)}
              </span>
            </div>
          </div>

          {/* Percentile distribution cards */}
          {benchmarkData.financial.percentiles && (
            <div className="glass-card rounded-2xl p-5 border border-slate-200 dark:border-slate-800 space-y-3">
              <h3 className="text-sm font-extrabold text-slate-900 dark:text-white">
                Physician Claim Amount Distribution Quartiles
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-1 font-mono">
                <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/60">
                  <span className="text-[10px] text-slate-400 uppercase font-sans font-bold block">25th Percentile</span>
                  <span className="text-base font-bold text-slate-800 dark:text-slate-200">
                    {formatCurrency(benchmarkData.financial.percentiles.p25 || 0)}
                  </span>
                </div>
                <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/60">
                  <span className="text-[10px] text-slate-400 uppercase font-sans font-bold block">50th (Median)</span>
                  <span className="text-base font-bold text-emerald-600 dark:text-emerald-400">
                    {formatCurrency(benchmarkData.financial.percentiles.p50 || 0)}
                  </span>
                </div>
                <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/60">
                  <span className="text-[10px] text-slate-400 uppercase font-sans font-bold block">75th Percentile</span>
                  <span className="text-base font-bold text-slate-800 dark:text-slate-200">
                    {formatCurrency(benchmarkData.financial.percentiles.p75 || 0)}
                  </span>
                </div>
                <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800/60">
                  <span className="text-[10px] text-slate-400 uppercase font-sans font-bold block">90th Percentile</span>
                  <span className="text-base font-bold text-amber-600 dark:text-amber-400">
                    {formatCurrency(benchmarkData.financial.percentiles.p90 || 0)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════════
          TAB CONTENT 7: ANOMALY & RISK RATE
         ════════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'risk' && benchmarkData?.risk && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="glass-card rounded-2xl p-5 border border-slate-200 dark:border-slate-800">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Claims Requiring Review</span>
              <span className="text-2xl font-black font-mono text-rose-600 dark:text-rose-400">
                {benchmarkData.risk.fraud_risk_rate}%
              </span>
              <span className="text-xs text-slate-500 block mt-1">
                Peer benchmark rate: {benchmarkData.risk.peer_fraud_risk_rate}%
              </span>
            </div>

            <div className="glass-card rounded-2xl p-5 border border-slate-200 dark:border-slate-800">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Average Anomaly Score</span>
              <span className="text-2xl font-black font-mono text-slate-900 dark:text-white">
                {benchmarkData.risk.avg_fraud_score.toFixed(3)}
              </span>
              <span className="text-xs text-slate-500 block mt-1">
                Peer avg score: {benchmarkData.risk.peer_avg_fraud_score?.toFixed(3)}
              </span>
            </div>

            <div
              onClick={() => openDrilldown('risk', '', 'Audit-Flagged Anomaly Claims')}
              className="glass-card rounded-2xl p-5 border border-slate-200 dark:border-slate-800 hover:border-rose-500 cursor-pointer transition-all"
            >
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Flagged Claims Count</span>
              <span className="text-2xl font-black font-mono text-rose-600 dark:text-rose-400 flex items-center gap-1.5">
                {benchmarkData.risk.high_risk_claims_count}
                <ExternalLink className="w-4 h-4 text-slate-400" />
              </span>
              <span className="text-xs text-slate-500 block mt-1">
                Click to inspect audit queue
              </span>
            </div>
          </div>

          {/* Audit recommendation alert */}
          <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 flex items-start gap-3">
            <Info className="w-5 h-5 text-primary-500 shrink-0 mt-0.5" />
            <div className="text-xs leading-relaxed">
              <div className="font-bold text-slate-900 dark:text-white">
                Clinical Audit Recommendation:
              </div>
              <div className="text-slate-600 dark:text-slate-300 mt-0.5">
                {benchmarkData.risk.review_recommendation}
              </div>
              <div className="text-[11px] text-slate-400 mt-2 italic">
                * Note: Benchmarking indicators detect statistical variance compared with peers. They do not constitute determination of improper billing.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════════════════════════════════════════
          6. INTERACTIVE CLAIMS DRILL-DOWN MODAL
         ════════════════════════════════════════════════════════════════════════ */}
      {drilldownOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm animate-fade-in">
          <div className="bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden animate-scale-in">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary-100 dark:bg-primary-950 flex items-center justify-center text-primary-600 dark:text-primary-400 font-bold shrink-0">
                  <Stethoscope className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-black text-slate-900 dark:text-white">
                    {drilldownTitle || 'Claim Encounters Drill-Down'}
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Dr. {selectedPhysician} • {drilldownData?.total_count?.toLocaleString() || 0} claims matching criteria
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={exportDrilldownCsv}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 transition-colors cursor-pointer"
                  title="Export to CSV"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>Export</span>
                </button>
                <button
                  onClick={() => setDrilldownOpen(false)}
                  className="p-1.5 rounded-xl text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal Search Bar */}
            <div className="px-6 py-3 border-b border-slate-100 dark:border-slate-800/80 bg-slate-50 dark:bg-slate-800/40 flex items-center justify-between gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
                <input
                  type="text"
                  value={drilldownSearch}
                  onChange={(e) => setDrilldownSearch(e.target.value)}
                  placeholder="Filter this drill-down by invoice, patient, code..."
                  className="w-full pl-9 pr-3 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs text-slate-900 dark:text-white focus:outline-none"
                />
              </div>

              <span className="text-xs font-mono text-slate-500">
                Page {drilldownPage} of {drilldownData?.total_pages || 1}
              </span>
            </div>

            {/* Modal Table Content */}
            <div className="flex-1 overflow-auto p-6">
              {drilldownLoading ? (
                <div className="py-16 text-center space-y-3">
                  <RefreshCw className="w-8 h-8 text-primary-500 animate-spin mx-auto" />
                  <p className="text-xs text-slate-400 font-medium">Querying database claims...</p>
                </div>
              ) : (
                <div className="rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden shadow-sm">
                  <table className="w-full text-xs text-left">
                    <thead className="bg-slate-50 dark:bg-slate-800/80 text-[10px] uppercase font-black tracking-wider text-slate-400 border-b border-slate-200 dark:border-slate-700">
                      <tr>
                        <th className="py-3 px-4">Invoice / ID</th>
                        <th className="py-3 px-4">Patient</th>
                        <th className="py-3 px-4">Service Date</th>
                        <th className="py-3 px-4">ICD Diagnosis</th>
                        <th className="py-3 px-4">CPT / Service</th>
                        <th className="py-3 px-4 text-right">Gross Amt</th>
                        <th className="py-3 px-4">Type</th>
                        <th className="py-3 px-4">Facility</th>
                        <th className="py-3 px-4">Audit Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-800 font-medium">
                      {displayedDrilldownClaims.map((claim) => (
                        <tr key={claim.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                          <td className="py-3 px-4 font-mono font-bold text-slate-900 dark:text-white">
                            {claim.invoice_no || claim.id.slice(0, 8)}
                          </td>
                          <td className="py-3 px-4 text-slate-700 dark:text-slate-300">
                            <div>{claim.patient_name}</div>
                            <div className="text-[10px] text-slate-400">{claim.member_id}</div>
                          </td>
                          <td className="py-3 px-4 font-mono text-slate-500">
                            {claim.service_date}
                          </td>
                          <td className="py-3 px-4">
                            <span className="font-bold text-slate-800 dark:text-slate-200">{claim.primary_diag_code}</span>
                            <span className="text-[10px] text-slate-400 block max-w-xs truncate">{claim.diag_description}</span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="font-mono font-bold text-primary-600 dark:text-primary-400">{claim.cpt_code}</span>
                            <span className="text-[10px] text-slate-400 block max-w-xs truncate">{claim.cpt_description}</span>
                          </td>
                          <td className="py-3 px-4 text-right font-mono font-bold text-slate-900 dark:text-white">
                            {formatCurrency(claim.gross_claim_amount)}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                              claim.service_type === 'Inpatient' ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300'
                            }`}>
                              {claim.service_type}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-slate-500">
                            {claim.provider_name}
                          </td>
                          <td className="py-3 px-4">
                            {claim.is_fraud ? (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300 flex items-center gap-1 w-max">
                                <ShieldAlert className="w-3 h-3 text-rose-500" />
                                Review
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 flex items-center gap-1 w-max">
                                <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                                Clean
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                      {displayedDrilldownClaims.length === 0 && (
                        <tr>
                          <td colSpan={9} className="py-8 text-center text-slate-400">
                            No claims found matching this drill-down query.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Modal Pagination Footer */}
            {drilldownData && drilldownData.total_pages > 1 && (
              <div className="px-6 py-3 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/40 flex items-center justify-between text-xs font-medium">
                <span className="text-slate-500">
                  Showing page {drilldownPage} of {drilldownData.total_pages} ({drilldownData.total_count.toLocaleString()} total claims)
                </span>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => handleDrilldownPageChange(drilldownPage - 1)}
                    disabled={drilldownPage <= 1}
                    className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 disabled:opacity-40 cursor-pointer"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDrilldownPageChange(drilldownPage + 1)}
                    disabled={drilldownPage >= drilldownData.total_pages}
                    className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 disabled:opacity-40 cursor-pointer"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PhysicianBenchmarkingView;
